import { EFinancingCompany, EFinancingProgram, ELoanPurpose, EMerchantType } from '@if/core/types'
import { useMerchantByParentId } from '@src/data/api/merchants-api/merchants-api'
import useResources from '@src/hooks/resources-hooks'
import { FinancingProgramConfigs, ResourceLibraryByCategory } from '@src/types/Constants'
import { Merchant } from '@src/types/Merchant'
import { MerchantResource } from '@src/types/MerchantResource'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/** The single invite-capable program a company sends invitations for. */
export const inviteProgramForCompany = (companyId: EFinancingCompany): EFinancingProgram =>
  companyId === EFinancingCompany.Iceberg ? EFinancingProgram.MechanicalWarranty : EFinancingProgram.Personal

/** Whether the merchant offers the invite program for the given company (the program the invite is sent under). */
export const merchantSupportsInviteMessages = (
  supportedPrograms: EFinancingProgram[],
  companyId: EFinancingCompany,
): boolean => supportedPrograms.includes(inviteProgramForCompany(companyId))

export const programSupportsDocumentUploadRequest = (program: EFinancingProgram): boolean =>
  FinancingProgramConfigs[program]?.supportsDocumentUploadRequest === true

const useChildMerchants = (merchant: Merchant): [boolean, Merchant[]] => {
  const isBanner = merchant.merchantTypeId === EMerchantType.Banner
  const [childMerchants] = useMerchantByParentId(isBanner ? merchant.id : '')
  return [isBanner, childMerchants]
}

export const useNewApplicationPrograms = (merchant: Merchant): EFinancingProgram[] => {
  const [isBanner, childMerchants] = useChildMerchants(merchant)

  return useMemo(() => {
    const merchantPrograms = merchant.supportedFinancingPrograms ?? []
    if (!isBanner) return merchantPrograms
    const inheritedPrograms = childMerchants
      .filter((childMerchant) => childMerchant.isActive)
      .flatMap((childMerchant) => childMerchant.supportedFinancingPrograms ?? [])
    return [...new Set([...merchantPrograms, ...inheritedPrograms])]
  }, [merchant.supportedFinancingPrograms, isBanner, childMerchants])
}

export const useMerchantResources = (merchant: Merchant): MerchantResource[] => {
  const { i18n } = useTranslation()
  const [isBanner, childMerchants] = useChildMerchants(merchant)
  const programs = useNewApplicationPrograms(merchant)
  const isIceberg = merchant.financingCompanyId === EFinancingCompany.Iceberg
  const documents = useResources(programs, i18n.resolvedLanguage ?? 'en')

  return useMemo(() => {
    if (isIceberg) return documents
    const categories = [merchant.serviceCategory]
    if (isBanner) {
      categories.push(...childMerchants.filter((child) => child.isActive).map((child) => child.serviceCategory))
    }
    return [
      ...new Set(
        categories
          .filter((category): category is ELoanPurpose => category != null)
          .map((category) => ResourceLibraryByCategory[category]),
      ),
    ]
  }, [isIceberg, documents, merchant.serviceCategory, isBanner, childMerchants])
}
